import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, DollarSign, Activity, TrendingUp, TrendingDown,
  ArrowUp, ArrowDown, Minus, Shield, Ban, Eye, Sparkles,
  AlertTriangle, FileWarning, UserMinus, Zap,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const fmtCurrency = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("en-CA").format(n || 0);

const PERIODS = [
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "all", label: "All Time" },
];

// ----------------------------------------------------------------------------
// Trend pill: ↑green / ↓red / —grey
const TrendPill = ({ pctChange, delta, invertSign = false }) => {
  if (pctChange === null || pctChange === undefined) {
    return (
      <div className="inline-flex items-center gap-1 text-[#94A3B8] text-xs font-medium">
        <Minus size={12} /> No prior data
      </div>
    );
  }
  // Invert sign for metrics where "up" is bad (e.g., churn)
  const isPositive = invertSign ? pctChange < 0 : pctChange > 0;
  const isNegative = invertSign ? pctChange > 0 : pctChange < 0;
  const color = isPositive ? "#10B981" : isNegative ? "#DC2626" : "#94A3B8";
  const Icon = pctChange === 0 ? Minus : pctChange > 0 ? ArrowUp : ArrowDown;
  const sign = pctChange > 0 ? "+" : "";
  return (
    <div
      className="inline-flex items-center gap-1 text-xs font-semibold"
      style={{ color }}
      data-testid={`trend-pill-${isPositive ? "up" : isNegative ? "down" : "flat"}`}
    >
      <Icon size={12} />
      <span>{sign}{Math.abs(pctChange).toFixed(1)}%</span>
      {delta !== undefined && (
        <span className="text-[#94A3B8] font-normal">
          ({delta > 0 ? "+" : ""}{typeof delta === "number" && Math.abs(delta) < 100
            ? delta.toFixed(0)
            : fmtNum(delta)})
        </span>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Summary card with KPI value + trend pill
const SummaryCard = ({ icon: Icon, label, value, sub, card, accent = "#6B4E8A", isCurrency = false, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-2xl bg-white border border-[#E5DFEC] p-5 shadow-sm hover:shadow-md transition-shadow"
    data-testid={testId}
  >
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-medium">{label}</p>
      <div className="rounded-xl p-2 flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
        <Icon size={18} strokeWidth={1.8} />
      </div>
    </div>
    <p className="text-3xl font-semibold text-[#6B4E8A]" data-testid={`${testId}-value`}>
      {isCurrency ? fmtCurrency(value) : fmtNum(value)}
    </p>
    {sub && <p className="text-xs text-[#64748B] mt-1">{sub}</p>}
    {card && (
      <div className="mt-3 pt-3 border-t border-[#FAF7F2]">
        <TrendPill pctChange={card.pct_change} delta={card.delta} />
      </div>
    )}
  </motion.div>
);

// ----------------------------------------------------------------------------
// Period segmented toggle
const PeriodToggle = ({ period, onChange }) => (
  <div
    className="inline-flex bg-[#FAF7F2] border border-[#E5DFEC] rounded-full p-1 gap-1"
    role="tablist"
    aria-label="Time period"
    data-testid="period-toggle"
  >
    {PERIODS.map((p) => (
      <button
        key={p.key}
        type="button"
        role="tab"
        aria-selected={period === p.key}
        onClick={() => onChange(p.key)}
        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
          period === p.key
            ? "bg-[#6B4E8A] text-white shadow-sm"
            : "text-[#64748B] hover:text-[#6B4E8A]"
        }`}
        data-testid={`period-${p.key}`}
      >
        {p.label}
      </button>
    ))}
  </div>
);

// ----------------------------------------------------------------------------
// Chart card wrapper
const ChartCard = ({ title, subtitle, callout, calloutAccent = "#10B981", children, testId }) => (
  <div className="rounded-2xl bg-white border border-[#E5DFEC] p-5 shadow-sm" data-testid={testId}>
    <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
      <div>
        <h3 className="text-base font-semibold text-[#6B4E8A]">{title}</h3>
        {subtitle && <p className="text-xs text-[#64748B] mt-0.5">{subtitle}</p>}
      </div>
      {callout && (
        <div
          className="rounded-xl px-3 py-1.5 text-xs font-semibold"
          style={{ background: `${calloutAccent}14`, color: calloutAccent }}
          data-testid={`${testId}-callout`}
        >
          {callout}
        </div>
      )}
    </div>
    <div className="h-64 w-full" style={{ minHeight: 256 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={256}>
        {children}
      </ResponsiveContainer>
    </div>
  </div>
);

// ----------------------------------------------------------------------------
// Main page
const FounderDashboard = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [memberGrowth, setMemberGrowth] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState(null);
  const [activeTrend, setActiveTrend] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [banned, setBanned] = useState([]);
  const [llmUsage, setLlmUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unbanTarget, setUnbanTarget] = useState(null);
  const [unbanLoading, setUnbanLoading] = useState(false);

  const loadAll = async (p = period) => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/founder/summary`, { params: { period: p } }),
        axios.get(`${API}/founder/member-growth`, { params: { period: p } }),
        axios.get(`${API}/founder/revenue-trend`, { params: { period: p } }),
        axios.get(`${API}/founder/active-trend`, { params: { period: p } }),
        axios.get(`${API}/founder/quick-stats`, { params: { period: p } }),
        axios.get(`${API}/founder/banned-members`),
        axios.get(`${API}/founder/llm-usage`, { params: { period: p } }),
      ]);
      const [s, mg, rt, at, qs, bb, lu] = results;
      if (s.status === "fulfilled") setSummary(s.value.data);
      if (mg.status === "fulfilled") setMemberGrowth(mg.value.data);
      if (rt.status === "fulfilled") setRevenueTrend(rt.value.data);
      if (at.status === "fulfilled") setActiveTrend(at.value.data);
      if (qs.status === "fulfilled") setQuickStats(qs.value.data);
      if (bb.status === "fulfilled") setBanned(bb.value.data);
      if (lu.status === "fulfilled") setLlmUsage(lu.value.data);
      const failures = results.filter((r) => r.status === "rejected").length;
      if (failures === results.length) {
        toast.error("Failed to load founder dashboard");
      } else if (failures > 0) {
        toast.warning(`${failures} of ${results.length} dashboard sections failed to load`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(period); /* eslint-disable-next-line */ }, [period]);

  const handleUnban = async () => {
    if (!unbanTarget) return;
    setUnbanLoading(true);
    try {
      await axios.post(`${API}/admin/users/${unbanTarget.user_id}/unban`);
      toast.success(`${unbanTarget.name} has been unbanned`);
      setUnbanTarget(null);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unban");
    } finally {
      setUnbanLoading(false);
    }
  };

  const periodLabel = useMemo(() => PERIODS.find((p) => p.key === period)?.label || "", [period]);

  if (loading || !summary) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#8DB5A0] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const cards = summary.cards;
  const netNew = memberGrowth?.net_new ?? 0;
  const netNewLabel = `${netNew > 0 ? "+" : ""}${fmtNum(netNew)} net new in ${periodLabel}`;
  const netNewAccent = netNew >= 0 ? "#10B981" : "#DC2626";

  const revenueSum = (revenueTrend?.series || []).reduce((s, r) => s + (r.amount || 0), 0);
  const revenueCallout = `${fmtCurrency(revenueSum)} total in ${periodLabel}`;

  const peakActive = Math.max(...((activeTrend?.series || []).map((d) => d.active_users)), 0);
  const activeCallout = `Peak: ${fmtNum(peakActive)} active`;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#8DB5A0] font-semibold">
              <Sparkles size={14} /> Founder
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#6B4E8A] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Hi {user?.name?.split(" ")[0]}, here's your DHDH snapshot.
            </h1>
            <p className="text-[#64748B] text-sm mt-1">
              <Link to="/admin" className="text-[#8DB5A0] hover:underline" data-testid="admin-link">
                Open admin tools →
              </Link>
            </p>
          </div>
          <PeriodToggle period={period} onChange={setPeriod} />
        </div>

        {/* SUMMARY ROW */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <SummaryCard
            testId="kpi-total-members"
            icon={Users}
            label="Total Members"
            value={cards.total_members.value}
            card={cards.total_members}
            accent="#6B4E8A"
          />
          <SummaryCard
            testId="kpi-active-members"
            icon={Activity}
            label="Active Members"
            value={cards.active_members.value}
            sub={cards.active_members.window_label}
            card={cards.active_members}
            accent="#0EA5E9"
          />
          <SummaryCard
            testId="kpi-total-revenue"
            icon={DollarSign}
            label="Total Revenue"
            value={cards.total_revenue.value}
            card={cards.total_revenue}
            accent="#10B981"
            isCurrency
          />
          <SummaryCard
            testId="kpi-period-revenue"
            icon={TrendingUp}
            label={cards.period_revenue.label}
            value={cards.period_revenue.value}
            card={cards.period_revenue}
            accent="#8DB5A0"
            isCurrency
          />
        </div>

        {/* CHART ROW: Member growth + Revenue trend */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard
            testId="chart-member-growth"
            title="Member Growth"
            subtitle={periodLabel}
            callout={netNewLabel}
            calloutAccent={netNewAccent}
          >
            <AreaChart data={memberGrowth?.series || []}>
              <defs>
                <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6B4E8A" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#6B4E8A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5DFEC" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5DFEC", fontSize: 12 }} />
              <Area type="monotone" dataKey="total_members" stroke="#6B4E8A" strokeWidth={2.5} fill="url(#growthGrad)" />
            </AreaChart>
          </ChartCard>

          <ChartCard
            testId="chart-revenue"
            title="Revenue Trend"
            subtitle={`${periodLabel}, by ${revenueTrend?.bucket || "day"}`}
            callout={revenueCallout}
            calloutAccent="#10B981"
          >
            <BarChart data={revenueTrend?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5DFEC" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #E5DFEC", fontSize: 12 }}
                formatter={(v) => fmtCurrency(v)}
              />
              <Bar dataKey="amount" fill="#10B981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartCard>
        </div>

        {/* CHART: Active members trend (full width) */}
        <ChartCard
          testId="chart-active-trend"
          title="Active Members Trend"
          subtitle={`${periodLabel}, distinct users by ${activeTrend?.bucket || "day"}`}
          callout={activeCallout}
          calloutAccent="#0EA5E9"
        >
          <LineChart data={activeTrend?.series || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5DFEC" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} allowDecimals={false} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5DFEC", fontSize: 12 }} />
            <Line type="monotone" dataKey="active_users" stroke="#0EA5E9" strokeWidth={2.5} dot={{ r: 3, fill: "#0EA5E9" }} />
          </LineChart>
        </ChartCard>

        {/* QUICK STATS FOOTER */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="quick-stats-footer">
          <div className="rounded-2xl bg-white border border-[#E5DFEC] p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-medium">Pending Verifications</p>
                <p className="text-2xl font-semibold mt-2" style={{ color: quickStats?.pending_verifications > 0 ? "#8DB5A0" : "#6B4E8A" }}>
                  {fmtNum(quickStats?.pending_verifications ?? 0)}
                </p>
              </div>
              <div className="rounded-xl p-2.5 bg-[#8DB5A0]/10 text-[#8DB5A0]">
                <AlertTriangle size={18} />
              </div>
            </div>
            {quickStats?.pending_verifications > 0 && (
              <Link to="/admin?tab=verifications" className="mt-3 inline-block text-xs text-[#8DB5A0] font-medium hover:underline" data-testid="link-verifications">
                Review now →
              </Link>
            )}
          </div>

          <div className="rounded-2xl bg-white border border-[#E5DFEC] p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-medium">Flagged Content</p>
                <p className="text-2xl font-semibold mt-2" style={{ color: quickStats?.flagged_content > 0 ? "#DC2626" : "#6B4E8A" }}>
                  {fmtNum(quickStats?.flagged_content ?? 0)}
                </p>
              </div>
              <div className="rounded-xl p-2.5 bg-[#DC2626]/10 text-[#DC2626]">
                <FileWarning size={18} />
              </div>
            </div>
            {quickStats?.flagged_content > 0 && (
              <Link to="/admin?tab=flagged" className="mt-3 inline-block text-xs text-[#DC2626] font-medium hover:underline" data-testid="link-flagged">
                Review now →
              </Link>
            )}
          </div>

          <div className="rounded-2xl bg-white border border-[#E5DFEC] p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-medium">Churn Rate</p>
                <p className="text-2xl font-semibold mt-2 text-[#6B4E8A]">
                  {(quickStats?.churn?.rate_pct ?? 0).toFixed(1)}%
                </p>
                <p className="text-xs text-[#64748B] mt-0.5">
                  {fmtNum(quickStats?.churn?.count ?? 0)} member{quickStats?.churn?.count === 1 ? "" : "s"} left in {periodLabel.toLowerCase()}
                </p>
              </div>
              <div className="rounded-xl p-2.5 bg-[#F59E0B]/10 text-[#F59E0B]">
                <UserMinus size={18} />
              </div>
            </div>
          </div>
        </div>

        {/* LLM USAGE (beta cost monitor) */}
        {llmUsage && (
          <div className="rounded-2xl bg-white border border-[#E5DFEC] p-5 shadow-sm" data-testid="llm-usage-panel">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={16} className="text-[#8DB5A0]" />
              <h2 className="text-base font-semibold text-[#6B4E8A]">AI usage & costs</h2>
              <span className="text-xs text-[#64748B] ml-2">{periodLabel}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl bg-[#FAF7F2] border border-[#E5DFEC] p-3">
                <div className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Total LLM calls</div>
                <div className="text-2xl font-bold text-[#6B4E8A] mt-1">{fmtNum(llmUsage.total_llm_calls)}</div>
              </div>
              <div className="rounded-xl bg-[#FAF7F2] border border-[#E5DFEC] p-3">
                <div className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Premium (Hygiena)</div>
                <div className="text-2xl font-bold text-[#6B4E8A] mt-1">{fmtNum(llmUsage.models?.premium_calls || 0)}</div>
                <div className="text-[10px] text-[#64748B] mt-0.5">gpt-5.2</div>
              </div>
              <div className="rounded-xl bg-[#FAF7F2] border border-[#E5DFEC] p-3">
                <div className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Fast (moderation)</div>
                <div className="text-2xl font-bold text-[#6B4E8A] mt-1">{fmtNum(llmUsage.models?.fast_calls || 0)}</div>
                <div className="text-[10px] text-[#64748B] mt-0.5">gpt-4o-mini</div>
              </div>
              <div className="rounded-xl bg-[#10B981]/5 border border-[#10B981]/20 p-3">
                <div className="text-xs text-[#10B981] uppercase tracking-wider font-medium">Cache hits (free)</div>
                <div className="text-2xl font-bold text-[#10B981] mt-1">+{fmtNum(llmUsage.breakdown?.distance_cache_hits || 0)}</div>
                <div className="text-[10px] text-[#64748B] mt-0.5">distance lookups</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3 text-xs">
              <div className="rounded-lg border border-[#E5DFEC] p-2">
                <div className="text-[#94A3B8]">Hygiena (LLM)</div>
                <div className="font-semibold text-[#6B4E8A]">{fmtNum(llmUsage.breakdown?.hygiena_llm_messages || 0)}</div>
              </div>
              <div className="rounded-lg border border-[#10B981]/30 bg-[#10B981]/5 p-2">
                <div className="text-[#10B981]">Slash cmds (free)</div>
                <div className="font-semibold text-[#10B981]">{fmtNum(llmUsage.breakdown?.hygiena_slash_commands || 0)}</div>
              </div>
              <div className="rounded-lg border border-[#E5DFEC] p-2">
                <div className="text-[#94A3B8]">CE certs</div>
                <div className="font-semibold text-[#6B4E8A]">{fmtNum(llmUsage.breakdown?.ce_certificates_extracted || 0)}</div>
              </div>
              <div className="rounded-lg border border-[#E5DFEC] p-2">
                <div className="text-[#94A3B8]">Receipts</div>
                <div className="font-semibold text-[#6B4E8A]">{fmtNum(llmUsage.breakdown?.expense_receipts_analyzed || 0)}</div>
              </div>
              <div className="rounded-lg border border-[#E5DFEC] p-2">
                <div className="text-[#94A3B8]">Events</div>
                <div className="font-semibold text-[#6B4E8A]">{fmtNum(llmUsage.breakdown?.events_legitimacy_checked || 0)}</div>
              </div>
              <div className="rounded-lg border border-[#E5DFEC] p-2">
                <div className="text-[#94A3B8]">Reviews</div>
                <div className="font-semibold text-[#6B4E8A]">{fmtNum(llmUsage.breakdown?.clinic_reviews_moderated || 0)}</div>
              </div>
            </div>
            {llmUsage.top_hygiena_users && llmUsage.top_hygiena_users.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#FAF7F2]">
                <div className="text-xs uppercase tracking-wider font-medium text-[#94A3B8] mb-2">Top Hygiena users</div>
                <div className="space-y-1">
                  {llmUsage.top_hygiena_users.slice(0, 5).map((u) => (
                    <div key={u.user_id} className="flex justify-between text-xs py-1">
                      <span className="text-[#6B4E8A]">{u.user?.name || u.user?.email || u.user_id}</span>
                      <span className="text-[#64748B] font-mono">{fmtNum(u.count)} msgs</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BANNED MEMBERS */}
        <div className="rounded-2xl bg-white border border-[#E5DFEC] p-5 shadow-sm" data-testid="banned-members-section">
          <div className="flex items-center gap-2 mb-1">
            <Shield size={16} className="text-[#DC2626]" />
            <h2 className="text-base font-semibold text-[#6B4E8A]">Banned members</h2>
            <span className="text-xs text-[#64748B] ml-2">Manually suspended by admins</span>
          </div>
          {banned.length === 0 ? (
            <div className="text-center py-8 text-[#94A3B8] text-sm">No banned members. Nothing to review.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#E5DFEC]">
                    <th className="text-left py-2 px-2">Member</th>
                    <th className="text-left py-2 px-2">Reason</th>
                    <th className="text-left py-2 px-2">Banned at</th>
                    <th className="text-right py-2 px-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {banned.map((m) => (
                    <tr key={m.user_id} className="border-b border-[#FAF7F2]" data-testid={`banned-row-${m.user_id}`}>
                      <td className="py-3 px-2">
                        <div className="font-medium text-[#6B4E8A]">{m.name}</div>
                        <div className="text-xs text-[#64748B]">{m.email}</div>
                      </td>
                      <td className="py-3 px-2 text-xs text-[#64748B]">{m.ban_reason || "—"}</td>
                      <td className="py-3 px-2 text-[#64748B] text-xs">
                        {m.banned_at ? new Date(m.banned_at).toLocaleDateString("en-CA") : "—"}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#8DB5A0] text-[#8DB5A0] hover:bg-[#8DB5A0] hover:text-white"
                          onClick={() => setUnbanTarget(m)}
                          data-testid={`unban-btn-${m.user_id}`}
                        >
                          <Eye size={14} className="mr-1.5" /> Review & unban
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Unban dialog */}
      <Dialog open={!!unbanTarget} onOpenChange={(open) => { if (!open) { setUnbanTarget(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="unban-dialog">
          <DialogHeader>
            <DialogTitle>Unban {unbanTarget?.name}?</DialogTitle>
            <DialogDescription>
              They'll regain access immediately.
            </DialogDescription>
          </DialogHeader>
          {unbanTarget?.ban_reason && (
            <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-lg p-3 text-xs space-y-1">
              <div className="text-[#94A3B8] uppercase tracking-wider font-medium">Ban reason</div>
              <div className="text-[#6B4E8A]">{unbanTarget.ban_reason}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnbanTarget(null)} disabled={unbanLoading} data-testid="unban-cancel-btn">Cancel</Button>
            <Button
              className="bg-[#8DB5A0] hover:bg-[#C8634A] text-white"
              onClick={handleUnban}
              disabled={unbanLoading}
              data-testid="unban-confirm-btn"
            >
              {unbanLoading ? "Unbanning..." : "Unban member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default FounderDashboard;
